import React, { useEffect, useMemo, useState } from 'react';
import type { ModeContextProps } from './Contexts/ModeContext';
import ModeContext from './Contexts/ModeContext';
import Mode from './Enums/Mode';
import ConnectPage from './Pages/ConnectPage';
import ReLoginPage from './Pages/ReLoginPage';
import { ThemeProvider } from '@mui/material/styles';
import type { SettingsContextProps } from './Contexts/SettingsContext';
import SettingsContext from './Contexts/SettingsContext';
import darkModeTheme from './Themes/DarkModeTheme';
import lightModeTheme from './Themes/LightModeTheme';
import AppTheme from './Enums/AppTheme';
import PageWrapperComponent from './Components/PageWrapper/PageWrapperComponent';
import type { JSX } from 'react';
import ApiService from './Services/ApiService';
import LocalStorageService from './Services/LocalStorageService';
import type { LocalStorageContextProps } from './Contexts/LocalStorageContext';
import LocalStorageContext from './Contexts/LocalStorageContext';
import type CurrentUser from './Models/CurrentUserModel';
import type SystemInfo from './Models/SystemInfoModel';
import type AppConfig from './Models/AppConfigModel';
import IndexedDBService from './Services/IndexedDBService';
import { appVersion, minApiVersion } from './Constants';
import ErrorPage from './Pages/ErrorPage';
import type { ApiContextProps } from './Contexts/ApiContext';
import ApiContext from './Contexts/ApiContext';
import type { SystemContextProps } from './Contexts/SystemContext';
import SystemContext from './Contexts/SystemContext';
import CreatePinPage from './Pages/CreatePinPage';
import type { IndexedDBContextProps } from './Contexts/IndexedDBContext';
import IndexedDBContext from './Contexts/IndexedDBContext';
import type { UserContextProps } from './Contexts/UserContext';
import UserContext from './Contexts/UserContext';
import type { TokenContextProps } from './Contexts/TokenContext';
import TokenContext from './Contexts/TokenContext';
import type Country from './Models/CountryModel';
import type User from './Models/UserModel';
import type ChecklistModel from './Models/Checklists/ChecklistModelModel';
import type InspectingCompany from './Models/InspectingCompanyModel';
import type UserDepartmentLink from './Models/UserDepartmentLinkModel';
import type Inspector from './Models/InspectorModel';
import type Department from './Models/DepartmentModel';
import type Category from './Models/CategoryModel';
import type TenantToken from './Models/TenantTokenModel';
import type KFleetToken from './Models/KFleetTokenModel';
import TokenType from './Enums/TokenType';
import type DocumentTreeNode from './Models/DocumentTreeNodeModel';
import type DocumentNode from './Models/DocumentNodeModel';
import MainPageRouter from './Routers/MainPageRouter';
import { checkVersion } from 'shouldve-been-js';
import { UpdateSnackbar, useServiceWorker } from 'react-pwa-tools';
import type MemoTemplate from './Models/MemoTemplateModel';
import type ObservationModel from './Models/ObservationModelModel';
import type ObservationChapter from './Models/ObservationChapterModel';
import type ObservationSubChapter from './Models/ObservationSubChapterModel';
import type ObservationRuleSet from './Models/ObservationRuleSetModel';

function App(): JSX.Element {

    const { waitingWorker, showReload, reloadPage } = useServiceWorker();
   
    const [mode, setMode] = useState<Mode>(Mode.CONNECT);
    const [theme, setTheme] = useState<AppTheme>(AppTheme.LIGHT_MODE);
    const [api, setApi] = useState<ApiService | null>(null);
    const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
    const [lastActivity, setLastActivity] = useState<Date | null>(null);
    const [hashedPin, setHashedPin] = useState<string | null>(null);
    const [health, setHealth] = useState<boolean>(false);

    const [systemName, setSystemName] = useState<string | null>(null);
    const [apiVersion, setApiVersion] = useState<string | null>(null);
    const [isOffice, setIsOffice] = useState<boolean>(false);
    const [minAppVersion, setMinAppVersion] = useState<string | null>(null);
    const [appEnabled, setAppEnabled] = useState<boolean>(false);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [serverTimezoneOffsetMinutes, setServerTimezoneOffsetMinutes] = useState<number | null>(null);
    const [tenantDisplayName, setTenantDisplayName] = useState<string | null>(null);

    const [token, setToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [expiration, setExpiration] = useState<Date | null>(null);
    const [refreshTokenExpired, setRefreshTokenExpired] = useState<boolean>(false);
    const [tokenType, setTokenType] = useState<TokenType>(TokenType.TENANT);

    //used to prevent error messages from showing before the application is initialized
    const [initializedFromLocalStorage, setInitializedFromLocalStorage] = useState<boolean>(false);
    const [initializedFromIndexedDB, setInitializedFromIndexedDB] = useState<boolean>(false);
    const [connectedDataFetched, setConnectedDataFetched] = useState<boolean>(false);

    const [errorMessageClient, setErrorMessageClient] = useState<string | null>(null);
    const [errorMessageSystem, setErrorMessageSystem] = useState<string | null>(null);
    
    const [refreshConnectedDataRerenderFlag, setRefreshConnectedDataRerenderFlag] = useState<Date>(new Date());
    const [updateSnackbarOpen, setUpdateSnackbarOpen] = useState<boolean>(false);

    const connected: boolean = mode !== Mode.CONNECT;
    
    const localStorageService: LocalStorageService = useMemo<LocalStorageService>(() => new LocalStorageService('experience'), []);
    const indexedDBService: IndexedDBService = useMemo<IndexedDBService>(() => new IndexedDBService('experience'), []);

    const modeContextValues: ModeContextProps = useMemo<ModeContextProps>(() => {
        return {
            mode: mode,
            setMode: setMode,
        };
    }, [mode]);

    const userContextValues: UserContextProps = useMemo<UserContextProps>(() => {
        return {
            currentUser: currentUser,
            setCurrentUser: setCurrentUser,
            lastActivity: lastActivity,
            setLastActivity: setLastActivity,
            hashedPin: hashedPin,
            setHashedPin: setHashedPin,
        };
    }, [currentUser, hashedPin, lastActivity]);

    const tokenContextValues: TokenContextProps = useMemo<TokenContextProps>(() => {
        return {
            token: token,
            setToken: setToken,
            refreshToken: refreshToken,
            setRefreshToken: setRefreshToken,
            expiration: expiration,
            setExpiration: setExpiration,
            tokenType: tokenType,
            setTokenType: setTokenType,
        };
    }, [expiration, refreshToken, token, tokenType]);

    const apiContextValues: ApiContextProps = useMemo<ApiContextProps>(() => {
        return {
            api: api,
            setApi: setApi,
            setRefreshConnectedDataRerenderFlag: setRefreshConnectedDataRerenderFlag,
        };
    }, [api]);

    const localStorageContextValues: LocalStorageContextProps = useMemo<LocalStorageContextProps>(() => {
        return {
            localStorageService: localStorageService,
        };
    }, [localStorageService]);

    const indexedDBContextValues: IndexedDBContextProps = useMemo<IndexedDBContextProps>(() => {
        return {
            indexedDBService: indexedDBService,
        };
    }, [indexedDBService]);

    const settingsContextValues: SettingsContextProps = useMemo<SettingsContextProps>(() => {
        return {
            theme: theme,
            setTheme: setTheme,
        };
    }, [theme]);

    const systemContextValues: SystemContextProps = useMemo<SystemContextProps>(() => {
        return {
            systemName: systemName,
            setSystemName: setSystemName,
            apiVersion: apiVersion,
            setApiVersion: setApiVersion,
            isOffice: isOffice,
            setIsOffice: setIsOffice,
            minAppVersion: minAppVersion,
            setMinAppVersion: setMinAppVersion,
            appEnabled: appEnabled,
            setAppEnabled: setAppEnabled,
            logoUrl: logoUrl,
            setLogoUrl: setLogoUrl,
            serverTimezoneOffsetMinutes: serverTimezoneOffsetMinutes,
            setServerTimezoneOffsetMinutes: setServerTimezoneOffsetMinutes,
            tenantDisplayName: tenantDisplayName,
            setTenantDisplayName: setTenantDisplayName,
        };
    }, [apiVersion, appEnabled, isOffice, logoUrl, minAppVersion, serverTimezoneOffsetMinutes, systemName, tenantDisplayName]);

    const renderBody: JSX.Element = useMemo<JSX.Element>(() => {

        if (errorMessageClient !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageClient}
                    canDisconnect={false}
                />
            );

        } else if (mode === Mode.CONNECT) {
            return <ConnectPage />; 

        } else if (errorMessageSystem !== null) {
            return (
                <ErrorPage 
                    errorMessage={errorMessageSystem}
                    canDisconnect={true}
                />
            );

        } else if (mode === Mode.CREATE_PIN) {
            return <CreatePinPage />;
            
        } else if (mode === Mode.RELOGIN) {
            return <ReLoginPage
                refreshTokenExpired={refreshTokenExpired}
            />;
            
        } else if (mode === Mode.MAIN) {
            return <MainPageRouter />;

        } else { //should not happen
            return <ConnectPage />;
        }
    }, [errorMessageClient, errorMessageSystem, mode, refreshTokenExpired]);

    // decides when to show the toast
    useEffect(() => {

        if (showReload && waitingWorker !== null) {
            setUpdateSnackbarOpen(true);
        } else {
            setUpdateSnackbarOpen(false);
        }
    }, [waitingWorker, showReload, reloadPage]);

    useEffect(() => { //check health

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function checkHealth(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                if (connected) {
                    const newHealth: boolean = await api?.getHealth(token) ?? false;

                    if (!mounted) {
                        return;
                    }

                    if (newHealth) {
                        setHealth(true);
                        return;
                    }

                    //if fails, wait 2 seconds and try once more before doing anything
                    await new Promise(r => setTimeout(r, 2000));
                    const retryHealth: boolean = await api?.getHealth(token) ?? false;

                    if (!mounted) {
                        return;
                    }

                    setHealth(retryHealth);
                } else {
                    setHealth(true);
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void checkHealth(); //run once immediately
        const interval: ReturnType<typeof setInterval> = setInterval(checkHealth, 10000); //run again every 10th second
         
        return () => {
            clearInterval(interval);
            mounted = false;
        };
    }, [api, connected, token]);

    useEffect(() => { //initialize values from local storage
     
        const newMode: Mode | null = localStorageService.getMode();
        const newTheme: AppTheme | null = localStorageService.getTheme();
        const newApi: ApiService | null = localStorageService.getApi();
        const newSystemName: string | null = localStorageService.getSystemName();
        const newApiVersion: string | null = localStorageService.getApiVersion();
        const newIsOffice: boolean = localStorageService.getIsOffice();
        const newMinAppVersion: string | null = localStorageService.getMinAppVersion();
        const newAppEnabled: boolean = localStorageService.getAppEnabled();
        const newServerTimezoneOffsetMinutes: number | null = localStorageService.getServerTimezoneOffsetMinutes();
        const newCurrentUser: CurrentUser | null = localStorageService.getCurrentUser();
        const newLastActivity: Date | null = localStorageService.getLastActivity();
        const newHashedPin: string | null = localStorageService.getHashedPin();
        const newToken: string | null = localStorageService.getToken();
        const newRefreshToken: string | null = localStorageService.getRefreshToken();
        const newExpiration: Date | null = localStorageService.getExpiration();
        const newTokenType: TokenType = localStorageService.getTokenType();
        const newTenantDisplayName: string | null = localStorageService.getTenantDisplayName();

        if (newMode !== null) {
            setMode(newMode);
        }

        if (newTheme !== null) {
            setTheme(newTheme);
        }

        if (newApi !== null) {
            setApi(newApi);
        }

        if (newSystemName !== null) {
            setSystemName(newSystemName);
        }

        if (newApiVersion !== null) {
            setApiVersion(newApiVersion);
        }

        setIsOffice(newIsOffice);

        if (newMinAppVersion !== null) {
            setMinAppVersion(newMinAppVersion);
        }

        setAppEnabled(newAppEnabled);

        if (newServerTimezoneOffsetMinutes !== null) {
            setServerTimezoneOffsetMinutes(newServerTimezoneOffsetMinutes);
        }

        if (newCurrentUser !== null) {
            setCurrentUser(newCurrentUser);
        }

        if (newLastActivity !== null) {
            setLastActivity(newLastActivity);
        }

        if (newHashedPin !== null) {
            setHashedPin(newHashedPin);
        }

        if (newToken !== null) {
            setToken(newToken);
        }

        if (newRefreshToken !== null) {
            setRefreshToken(newRefreshToken);
        }

        if (newExpiration !== null) {
            setExpiration(newExpiration);
        }

        setTokenType(newTokenType);

        if (newTenantDisplayName !== null) {
            setTenantDisplayName(newTenantDisplayName);
        }

        setInitializedFromLocalStorage(true);

    }, [localStorageService]);

    useEffect(() => { //initialize values from indexedDB

        let newLogoUrl: string = '';
        let mounted: boolean = true;

        async function fetchFromIndexedDB(): Promise<void> {

            const newLogoBlob: Blob | null = await indexedDBService.getLogoBlob();
            
            if (mounted) {
                if (newLogoBlob !== null) {
                    newLogoUrl = URL.createObjectURL(newLogoBlob);
                    setLogoUrl(newLogoUrl);
                }

                setInitializedFromIndexedDB(true);
            }
        }

        void fetchFromIndexedDB();

        return () => {

            if (newLogoUrl !== '') {
                URL.revokeObjectURL(newLogoUrl);
            }
            
            mounted = false;
        };

    }, [indexedDBService]);

    useEffect(() => { //fetch this info when connected (fast, most vital info)

        let mounted: boolean = true;
        let newLogoUrl: string = '';
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function fetchVitalConnectedData(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;
            
            try {
                const systemInfo: SystemInfo | null = await api?.getSystemInfo(token) ?? null;
                const appConfig: AppConfig | null = await api?.getAppConfig(token) ?? null;
                const logoBlob: Blob | null = await api?.getCompanyLogo(token) ?? null;
                const newServerTimezoneOffsetMinutes: number | null = await api?.getServerTimezoneOffsetMinutes(token) ?? null;
                
                if (mounted) {

                    if (systemInfo !== null) {
                        setSystemName(systemInfo.systemName);
                        setApiVersion(systemInfo.apiVersion);
                        setIsOffice(systemInfo.isOffice);

                        localStorageService.setSystemName(systemInfo.systemName);
                        localStorageService.setApiVersion(systemInfo.apiVersion);
                        localStorageService.setIsOffice(systemInfo.isOffice);
                    }

                    if (appConfig !== null) {
                        setMinAppVersion(appConfig.minAppVersion);
                        setAppEnabled(appConfig.appEnabled);
            
                        localStorageService.setMinAppVersion(appConfig.minAppVersion);
                        localStorageService.setAppEnabled(appConfig.appEnabled);
                    }

                    if (logoBlob !== null) {
                        newLogoUrl = URL.createObjectURL(logoBlob);
                        setLogoUrl(newLogoUrl);

                        await indexedDBService.setLogoBlob(logoBlob);
                    }

                    if (newServerTimezoneOffsetMinutes !== null) {
                        setServerTimezoneOffsetMinutes(newServerTimezoneOffsetMinutes);

                        localStorageService.setServerTimezoneOffsetMinutes(newServerTimezoneOffsetMinutes);
                    }

                    setConnectedDataFetched(true);
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        if (connected) {
            void fetchVitalConnectedData(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(fetchVitalConnectedData, 300000); //run again every 5th minute
            
            return () => {

                if (newLogoUrl !== '') {
                    URL.revokeObjectURL(newLogoUrl);
                }

                clearInterval(interval);
                mounted = false;
            };
        } else {
            setConnectedDataFetched(false);
            return undefined;
        }
    }, [api, connected, indexedDBService, localStorageService, token, refreshConnectedDataRerenderFlag]);

    useEffect(() => { //fetch this info when connected (not as vital)

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time
        
        async function fetchOtherConnectedData(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;
            
            try {
                const countries: Country[] = await api?.getCountries(token) ?? [];
                const users: User[] = await api?.getAllUsers(token) ?? [];
                const checklistModels: ChecklistModel[] = await api?.getAllChecklistModels(token) ?? [];
                const inspectors: Inspector[] = await api?.getInspectors(token) ?? [];
                const inspectingCompanies: InspectingCompany[] = await api?.getInspectingCompanies(token) ?? [];
                const departments: Department[] = await api?.getDepartments(token) ?? [];
                const userDepartmentLinks: UserDepartmentLink[] = await api?.getDepartmentsForAllUsersAndRoles(token) ?? [];
                const categories: Category[] = await api?.getCategories(token) ?? [];
                const memoTemplates: MemoTemplate[] = await api?.getMemoTemplates(token) ?? [];
                const observationModels: ObservationModel[] = await api?.getAllObservationModels(token) ?? [];
                const observationChapters: ObservationChapter[] = await api?.getObservationChapters(token) ?? [];
                const observationSubChapters: ObservationSubChapter[] = await api?.getObservationSubChapters(token) ?? [];
                const observationRuleSets: ObservationRuleSet[] = await api?.getObservationRuleSets(token) ?? [];
            
                if (mounted) {

                    if (countries.length > 0) {
                        await indexedDBService.replaceAllCountries(countries);
                    }

                    if (users.length > 0) {
                        await indexedDBService.replaceAllUsers(users);
                    }

                    if (checklistModels.length > 0) {
                        await indexedDBService.replaceAllChecklistModels(checklistModels);
                    }

                    if (inspectors.length > 0) {
                        await indexedDBService.replaceAllInspectors(inspectors);
                    }

                    if (inspectingCompanies.length > 0) {
                        await indexedDBService.replaceAllInspectingCompanies(inspectingCompanies);
                    }

                    if (departments.length > 0) {
                        await indexedDBService.replaceAllDepartments(departments);
                    }

                    if (userDepartmentLinks.length > 0) {
                        await indexedDBService.replaceAllUserDepartmentLinks(userDepartmentLinks);
                    }

                    if (categories.length > 0) {
                        await indexedDBService.replaceAllCategories(categories);
                    }

                    if (memoTemplates.length > 0) {
                        await indexedDBService.replaceAllMemoTemplates(memoTemplates);
                    }

                    if (observationModels.length > 0) {
                        await indexedDBService.replaceAllObservationModels(observationModels);
                    }

                    if (observationChapters.length > 0) {
                        await indexedDBService.replaceAllObservationChapters(observationChapters);
                    }

                    if (observationSubChapters.length > 0) {
                        await indexedDBService.replaceAllObservationSubChapters(observationSubChapters);
                    }

                    if (observationRuleSets.length > 0) {
                        await indexedDBService.replaceAllObservationRuleSets(observationRuleSets);
                    }
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        if (connected) {
            void fetchOtherConnectedData(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(fetchOtherConnectedData, 300000); //run again every 5th minute
            
            return () => {
                clearInterval(interval);
                mounted = false;
            };
        } else {
            return undefined;
        }
    }, [api, connected, indexedDBService, token, refreshConnectedDataRerenderFlag]);

    useEffect(() => { //documents have their own useEffect as it might be a lot of data and we dont want to block the other data fetching

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time
        
        async function fetchMissingDocuments(newDocumentNodes: DocumentNode[], oldDocumentNodes: DocumentNode[]): Promise<DocumentNode[]> { //only fetch the documents that aren't already in place

            const newDocumentIds: string[] = newDocumentNodes.map((node: DocumentNode) => node.documentId);
            const oldDocumentIds: string[] = oldDocumentNodes.map((node: DocumentNode) => node.documentId);

            const documentsOnlyInNew: DocumentNode[] = newDocumentNodes.filter((node: DocumentNode) => !oldDocumentIds.includes(node.documentId));
            const documentsOnlyInOld: DocumentNode[] = oldDocumentNodes.filter((node: DocumentNode) => !newDocumentIds.includes(node.documentId));
            
            const documentsWithNewerRevision: DocumentNode[] = newDocumentNodes.filter((node: DocumentNode) => {
                
                const correspondingOldNode: DocumentNode | null = oldDocumentNodes.find((oldNode: DocumentNode) => oldNode.documentId === node.documentId) ?? null;

                if (correspondingOldNode === null) {
                    return false;
                }

                return node.sortNumber > correspondingOldNode.sortNumber; //the weird case where the old node has the higher sort number will not make a refetch happen (for now)
            });

            const documentsWithoutNewerRevision: DocumentNode[] = newDocumentNodes.filter((node: DocumentNode) => {
                
                const correspondingOldNode: DocumentNode | null = oldDocumentNodes.find((oldNode: DocumentNode) => oldNode.documentId === node.documentId) ?? null;

                if (correspondingOldNode === null) {
                    return false;
                }

                return node.sortNumber <= correspondingOldNode.sortNumber;
            });

            const updatedNewDocumentNodes: DocumentNode[] = [...newDocumentNodes]; //dont think a deep copy is necessary here

            //could consider parallelizing this part
            for (let i: number = 0; i < documentsOnlyInNew.length; i++) { //these should be fetched and inserted

                const currentDocument: DocumentNode | undefined = documentsOnlyInNew[i];

                if (currentDocument === undefined) {
                    continue;
                }

                // eslint-disable-next-line no-await-in-loop
                const newDocumentBlob: Blob | null = await api?.getDocument(currentDocument.documentId, currentDocument.sortNumber, token) ?? null;

                let success: boolean = false;

                if (newDocumentBlob !== null) {

                    if (!mounted) {
                        return [];
                    }

                    // eslint-disable-next-line no-await-in-loop
                    success = await indexedDBService.insertDocument(currentDocument.documentId, newDocumentBlob);
                }

                if (success) {

                    const correspondingUpdatedNewDocumentNode: DocumentNode | null = updatedNewDocumentNodes.find((node: DocumentNode) => node.documentId === currentDocument.documentId) ?? null;
                
                    if (correspondingUpdatedNewDocumentNode !== null) { //should never be null
                        correspondingUpdatedNewDocumentNode.documentStored = true;
                    }
                }
            }

            for (let i: number = 0; i < documentsOnlyInOld.length; i++) { //these should be deleted

                if (!mounted) {
                    return [];
                }

                const currentDocument: DocumentNode | undefined = documentsOnlyInOld[i];

                if (currentDocument !== undefined) {
                    // eslint-disable-next-line no-await-in-loop
                    await indexedDBService.removeDocument(currentDocument.documentId);
                }
            }

            for (let i: number = 0; i < documentsWithNewerRevision.length; i++) { //these should be fetched and updated

                const currentDocument: DocumentNode | undefined = documentsWithNewerRevision[i];
                
                if (currentDocument !== undefined) {

                    // eslint-disable-next-line no-await-in-loop
                    const newDocumentBlob: Blob | null = await api?.getDocument(currentDocument.documentId, currentDocument.sortNumber, token) ?? null;
    
                    let success: boolean = false;
    
                    if (newDocumentBlob !== null) {
    
                        if (!mounted) {
                            return [];
                        }
    
                        // eslint-disable-next-line no-await-in-loop
                        success = await indexedDBService.updateDocument(currentDocument.documentId, newDocumentBlob);
                    }
    
                    if (success) {
    
                        const correspondingUpdatedNewDocumentNode: DocumentNode | null = updatedNewDocumentNodes.find((node: DocumentNode) => node.documentId === currentDocument.documentId) ?? null;
                    
                        if (correspondingUpdatedNewDocumentNode !== null) { //should never be null
                            correspondingUpdatedNewDocumentNode.documentStored = true;
                        }
                    }
                }
            }

            for (let i: number = 0; i < documentsWithoutNewerRevision.length; i++) { //these should only update if the old one doesnt have documentStored

                const currentDocument: DocumentNode | undefined = documentsWithoutNewerRevision[i];

                if (currentDocument === undefined) { //should never be undefined
                    continue;
                }

                const correspondingOldNode: DocumentNode | null = oldDocumentNodes.find((oldNode: DocumentNode) => oldNode.documentId === currentDocument.documentId) ?? null;

                if (correspondingOldNode === null) { //should never be null
                    continue;
                }

                if (!correspondingOldNode.documentStored) {

                    let success: boolean = false;

                    // eslint-disable-next-line no-await-in-loop
                    const newDocumentBlob: Blob | null = await api?.getDocument(currentDocument.documentId, currentDocument.sortNumber, token) ?? null;

                    if (newDocumentBlob !== null) {

                        if (!mounted) {
                            return [];
                        }

                        // eslint-disable-next-line no-await-in-loop
                        success = await indexedDBService.updateDocument(currentDocument.documentId, newDocumentBlob); //insert might be more precise, but by using update, no error will be thrown if a document with the key exists anyway
                    }

                    if (success) {

                        const correspondingUpdatedNewDocumentNode: DocumentNode | null = updatedNewDocumentNodes.find((node: DocumentNode) => node.documentId === currentDocument.documentId) ?? null;
                    
                        if (correspondingUpdatedNewDocumentNode !== null) { //should never be null
                            correspondingUpdatedNewDocumentNode.documentStored = true;
                        }
                    }
                }
            }

            return updatedNewDocumentNodes;
        }

        async function fetchDocumentData(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                const documentTreeNodes: DocumentTreeNode[] = await api?.getDocumentTreeNodes(token) ?? [];

                if (mounted) {

                    if (documentTreeNodes.length > 0) {
                        await indexedDBService.replaceAllDocumentTreeNodes(documentTreeNodes);
                    }
                }

                const documentNodes: DocumentNode[] = await api?.getDocumentNodes(token) ?? [];
                const existingDocumentNodes: DocumentNode[] = await indexedDBService.getAllDocumentNodes();
                const updatedDocumentNodes: DocumentNode[] = await fetchMissingDocuments(documentNodes, existingDocumentNodes);

                if (mounted) {

                    if (documentNodes.length > 0) {
                        await indexedDBService.replaceAllDocumentNodes(updatedDocumentNodes);
                    }
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        if (connected) {
            void fetchDocumentData(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(fetchDocumentData, 300000); //run again every 5th minute
            
            return () => {
                clearInterval(interval);
                mounted = false;
            };
        } else {
            return undefined;
        }
    }, [api, connected, indexedDBService, token, refreshConnectedDataRerenderFlag]);

    useEffect(() => { //check if anything client related is wrong

        if (!IndexedDBService.isIndexedDBAvailable()) {
            setErrorMessageClient('Your browser does not support IndexedDB. This is required. Please try another browser.');
        } else if (!LocalStorageService.isLocalStorageAvailable()) {
            setErrorMessageClient('Your browser does not support localstorage. This is required. Please try another browser.');
        } else if (crypto.subtle === undefined) {
            setErrorMessageClient('Your browser does not support crypto.subtle. This is required. Please try another browser.');
        } else {
            setErrorMessageClient(null);
        }
    }, []);

    useEffect(() => { //check if anything system related is wrong

        if (!initializedFromLocalStorage || !initializedFromIndexedDB || !connectedDataFetched) { //not ready to check yet
            return;
        }

        if (connected) {

            if (!appEnabled) {
                setErrorMessageSystem('This system is not configured for using this app. Contact your system administrator.');
            } else if (!isOffice) {
                setErrorMessageSystem('You have connected to a vessel system. Only office systems are supported.');
            } else if (minAppVersion === null || appVersion === null || !checkVersion(appVersion, minAppVersion)) {
                setErrorMessageSystem('You are on app version ' + appVersion + ', while your system requires at least version ' + (minAppVersion ?? 'ERROR') + '. Close all other tabs of this application and restart it with internet connection to update.');
            } else if (minApiVersion === null || apiVersion === null || !checkVersion(apiVersion, minApiVersion)) {
                setErrorMessageSystem('You are connected to an API on version ' + (apiVersion ?? 'ERROR') + ', while the app requires at least version ' + minApiVersion + '. Contact your system administrator.');
            } else {
                setErrorMessageSystem(null);
            }
        } else {
            setErrorMessageSystem(null);
        }
    }, [apiVersion, appEnabled, connected, connectedDataFetched, initializedFromIndexedDB, initializedFromLocalStorage, isOffice, minAppVersion]);

    useEffect(() => { //check for token expiration

        let mounted: boolean = true;
        let isRunning: boolean = false; //prevents multiple intervals to run at the same time

        async function checkTokenExpiration(): Promise<void> {

            if (isRunning) {
                return;
            }

            isRunning = true;

            try {
                setRefreshTokenExpired(false);

                if (refreshToken === null || expiration === null || currentUser === null || !health) { //don't check if there is no token or no connection, the health check and dependency will also make this check be run every time connection is reestablished which is good
                    return;
                }
        
                if (expiration.getTime() < Date.now() + 2 * 60 * 1000) { //if token expires in less than two minutes

                    if (tokenType === TokenType.TENANT) {

                        const tokenInfo: TenantToken | null | undefined = await ApiService.fetchNewTenantTokenByRefreshToken(refreshToken, indexedDBService);

                        if (mounted) {
                            if (tokenInfo === null) {

                                //refresh token has expired, send to relogin page and inform password login is required to go online, but can use pin to keep working offline
                                setRefreshTokenExpired(true);
                                return;
                            }

                            const newToken: string | null = tokenInfo?.token ?? null;
                            const newRefreshToken: string | null = tokenInfo?.refreshToken ?? null;
                            const newExpiration: Date | null = tokenInfo?.expiration ?? null;

                            if (newToken === null || newRefreshToken === null || newExpiration === null) {
                                return;
                            }

                            const newAccess: string[] = await api?.getUserAccess(currentUser.userId, newToken) ?? [];

                            const newCurrentUser: CurrentUser = currentUser.copy();
                            newCurrentUser.access = newAccess;

                            setCurrentUser(newCurrentUser);
                            localStorageService.setCurrentUser(newCurrentUser);

                            setToken(newToken);
                            setRefreshToken(newRefreshToken);
                            setExpiration(newExpiration);
                            localStorageService.setToken(newToken);
                            localStorageService.setRefreshToken(newRefreshToken);
                            localStorageService.setExpiration(newExpiration);
                        }
                    } else {

                        const tokenInfo: KFleetToken | null | undefined = await api?.fetchNewKFleetTokenByRefreshToken(refreshToken, indexedDBService);

                        if (mounted) {
                            if (tokenInfo === null) {

                                //refresh token has expired, send to relogin page and inform password login is required to go online, but can use pin to keep working offline
                                setRefreshTokenExpired(true);
                                return;
                            }

                            const newToken: string | null = tokenInfo?.token ?? null;
                            const newRefreshToken: string | null = tokenInfo?.refreshToken ?? null;
                            const newAccess: string[] | null = tokenInfo?.access ?? null;
                            const newExpiration: Date | null = tokenInfo?.expiration ?? null;

                            if (newToken === null || newRefreshToken === null || newAccess === null || newExpiration === null) {
                                return;
                            }

                            const newCurrentUser: CurrentUser = currentUser.copy();
                            newCurrentUser.access = newAccess;

                            setCurrentUser(newCurrentUser);
                            localStorageService.setCurrentUser(newCurrentUser);

                            setToken(newToken);
                            setRefreshToken(newRefreshToken);
                            setExpiration(newExpiration);
                            localStorageService.setToken(newToken);
                            localStorageService.setRefreshToken(newRefreshToken);
                            localStorageService.setExpiration(newExpiration);
                        }
                    }
                }
            } finally {
                // eslint-disable-next-line require-atomic-updates
                isRunning = false;
            }
        }

        void checkTokenExpiration();
        const interval: ReturnType<typeof setInterval> = setInterval(checkTokenExpiration, 60000); //run again every minute

        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [api, currentUser, expiration, health, indexedDBService, localStorageService, refreshToken, token, tokenType]);

    useEffect(() => { //check if timed out

        function checkTimeout(): void {
           
            const timeoutHours: number = 1; //times out after 1 hour

            if (lastActivity === null || lastActivity.getTime() < (new Date()).getTime() - (1000 * 60 * 60 * timeoutHours)) {

                //this code should reflect what is done in handleLogout, but is currently duplicated as the contexts are not available here
                setMode(Mode.RELOGIN);
                localStorageService.setMode(Mode.RELOGIN);
            }
        }

        if (initializedFromLocalStorage && mode === Mode.MAIN) {
            checkTimeout(); //run once immediately
            const interval: ReturnType<typeof setInterval> = setInterval(checkTimeout, 60000); //run again every minute
            
            return () => clearInterval(interval);
        } else {
            return undefined;
        }
    }, [initializedFromLocalStorage, lastActivity, localStorageService, mode]);

    return (
        <ModeContext.Provider value={modeContextValues}>
            <UserContext.Provider value={userContextValues}>
                <TokenContext.Provider value={tokenContextValues}>
                    <ApiContext.Provider value={apiContextValues}>
                        <LocalStorageContext.Provider value={localStorageContextValues}>
                            <IndexedDBContext.Provider value={indexedDBContextValues}>
                                <SettingsContext.Provider value={settingsContextValues}>
                                    <SystemContext.Provider value={systemContextValues}>
                                        <ThemeProvider theme={theme === AppTheme.DARK_MODE ? darkModeTheme : lightModeTheme}>
                                            <PageWrapperComponent
                                                renderBody={renderBody}
                                            />

                                            <UpdateSnackbar
                                                open={updateSnackbarOpen}
                                                setOpen={setUpdateSnackbarOpen}
                                                onRefresh={reloadPage}
                                            />
                                        </ThemeProvider>
                                    </SystemContext.Provider>
                                </SettingsContext.Provider>
                            </IndexedDBContext.Provider>
                        </LocalStorageContext.Provider>
                    </ApiContext.Provider>
                </TokenContext.Provider>
            </UserContext.Provider>
        </ModeContext.Provider>
    );
}

export default App;